import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Search, Heart, ShoppingCart, Menu, User } from 'lucide-react';
import { Container } from './layout/Container';
import { SearchOverlay } from './ui/SearchOverlay';
import { MenuPanel } from './ui/MenuPanel';
import { CartPanel } from './ui/CartPanel';
import Button from './ui/Button';
import { searchArtworks } from '../utils/search';
import { signOutUser } from '../store/slices/authSlice';
import useSmartCart from '../hooks/useSmartCart';
import { get_avatar_url, AVATAR_SIZES } from '../utils/formatters';
import { should_hide_onboarding_banner } from '../utils/onboarding';

// Navigation items - from frontend (real routes)
const nav_items = [
  { label: 'Explorar', href: '/explorar' },
  { label: 'Artistas', href: '/artistas' },
  { label: 'Noticias', href: '/noticias' },
  { label: 'Propuestas', href: '/propuestas' },
];

/**
 * Navbar - Barra de navegación con glassmorphism
 *
 * Mobile: [☰] RAIOS ART [🔍] [🛒]
 * - Hamburguesa abre MenuPanel híbrido (navegación + usuario)
 * - Search y Cart siempre visibles
 *
 * Desktop: Logo + Nav Links + Search/Favorites/Cart + Avatar + CTA
 * - Settings removido (está en MenuPanel)
 */
const Navbar = () => {
  const navigate = useNavigate();
  const wishlist_items = useSelector(state => state.wishlist.items);
  const { is_authenticated, user } = useSelector(state => state.auth);

  const is_artist = user?.is_artist || false;

  // Banner de onboarding artista: oculto si el usuario es artista (BD) o si
  // dismisseo el banner (localStorage). Fallback a raios_artist_setup_completed
  // para usuarios que completaron onboarding antes de la migracion server-side.
  const [dismissed_state, set_dismissed_state] = useState(
    () => should_hide_onboarding_banner(user)
  );

  useEffect(() => {
    set_dismissed_state(should_hide_onboarding_banner(user));

    const sync_setup_state = () => {
      set_dismissed_state(should_hide_onboarding_banner(user));
    };
    window.addEventListener('raios_setup_completed', sync_setup_state);
    window.addEventListener('raios_banner_dismissed', sync_setup_state);
    window.addEventListener('storage', sync_setup_state);
    return () => {
      window.removeEventListener('raios_setup_completed', sync_setup_state);
      window.removeEventListener('raios_banner_dismissed', sync_setup_state);
      window.removeEventListener('storage', sync_setup_state);
    };
  }, [user]);

  const profile_setup_complete = dismissed_state;

  const dispatch = useDispatch();
  const { items: cart_items, removeFromCart, updateQuantity } = useSmartCart();
  const [is_search_open, set_is_search_open] = useState(false);
  const [search_results, set_search_results] = useState(null);
  const [is_search_loading, set_is_search_loading] = useState(false);
  const [recent_searches, set_recent_searches] = useState([]);
  const [is_menu_open, set_is_menu_open] = useState(false);
  const [is_cart_open, set_is_cart_open] = useState(false);

  // Cargar búsquedas recientes de localStorage
  useEffect(() => {
    const saved = localStorage.getItem('raios_recent_searches');
    if (saved) {
      try { set_recent_searches(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }
  }, []);

  const handle_search = async (query) => {
    if (!query) {
      set_search_results(null);
      return;
    }

    set_is_search_loading(true);
    const artworks = await searchArtworks(query);

    // Mapear a tipos agrupados que espera SearchOverlay
    // Nota: artworksService retorna category como objeto {id,name,slug} y artist como {id,full_name,...}
    const obra_results = artworks.map(a => ({
      type: 'artwork',
      id: a.id,
      title: a.title,
      artist_name: a.artist?.full_name || null,
      category: a.category?.name || null,
      price: a.price ? `$${Number(a.price).toLocaleString('es-CL')}` : null,
      image_url: a.images?.[0] || null,
    }));

    // Extraer artistas únicos
    const artist_map = {};
    artworks.forEach(a => {
      const name = a.artist?.full_name;
      if (!name) return;
      if (!artist_map[name]) artist_map[name] = 0;
      artist_map[name]++;
    });
    const artista_results = Object.entries(artist_map).map(([name, count]) => ({
      type: 'artist',
      id: name,
      name,
      works_count: count,
    }));

    // Extraer categorías únicas (category es un objeto {id,name,slug})
    const category_map = {};
    artworks.forEach(a => {
      if (!a.category?.name) return;
      category_map[a.category.name] = a.category.id;
    });
    const categoria_results = Object.entries(category_map).map(([name, id]) => ({
      type: 'category',
      id,
      name,
    }));

    set_search_results([...obra_results, ...artista_results, ...categoria_results]);
    set_is_search_loading(false);

    // Guardar en recientes
    set_recent_searches(prev => {
      const updated = [query, ...prev.filter(s => s !== query)].slice(0, 10);
      localStorage.setItem('raios_recent_searches', JSON.stringify(updated));
      return updated;
    });
  };

  const handle_select_result = (result) => {
    if (result.type === 'artwork') {
      navigate(`/obra/${result.id}`);
    } else if (result.type === 'artist') {
      navigate('/artistas');
    } else if (result.type === 'category') {
      navigate('/explorar');
    }
  };

  const handle_logout = () => {
    dispatch(signOutUser());
    set_is_menu_open(false);
  };

  const handle_login = () => {
    set_is_menu_open(false);
    navigate('/auth');
  };

  // Cart handlers - delegate to useSmartCart (handles auth routing + error fallback)
  const handle_update_quantity = (item_id, new_quantity) => {
    updateQuantity(item_id, new_quantity);
  };

  const handle_remove_item = (item_id) => {
    removeFromCart(item_id);
  };

  const handle_checkout = () => {
    set_is_cart_open(false);
    navigate('/checkout');
  };

  const handle_continue_shopping = () => {
    set_is_cart_open(false);
    navigate('/explorar');
  };

  return (
    <header className="nav-glass">
      <Container>
        <nav className="flex items-center justify-between h-[72px]">
          {/* Mobile: Hamburger + Logo */}
          <div className="flex md:hidden items-center gap-2">
            {/* Menu Toggle - Mobile (izquierda) */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="p-2 text-raios-text-high"
              onClick={() => set_is_menu_open(true)}
              aria-label="Abrir menú"
            >
              <Menu size={24} />
            </motion.button>

            {/* Logo - Mobile */}
            <Link to="/" className="inline-block">
              <span className="font-mono text-lg font-bold text-raios-text-high">
                RAIOS
              </span>
              <span className="font-mono text-[10px] text-raios-primary tracking-widest ml-1">
                ARTE
              </span>
            </Link>
          </div>

          {/* Desktop: Logo */}
          <Link to="/" className="hidden md:inline-block flex-shrink-0">
            <span className="font-mono text-xl font-bold text-raios-text-high">
              RAIOS
            </span>
            <span className="font-mono text-xs text-raios-primary tracking-widest ml-1">
              ARTE
            </span>
          </Link>

          {/* Nav Links - Desktop */}
          <div className="hidden md:flex items-center gap-8">
            {nav_items.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="font-sans text-sm text-raios-text-support hover:text-raios-text-high transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Actions - Desktop */}
          <div className="hidden md:flex items-center gap-4">
            {/* Search */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => set_is_search_open(true)}
              className="p-2 text-raios-text-support hover:text-raios-text-high transition-colors"
              aria-label="Buscar"
            >
              <Search size={20} />
            </motion.button>

            {/* Favorites */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/favoritos')}
              className="p-2 text-raios-text-support hover:text-raios-text-high transition-colors relative"
              aria-label="Favoritos"
            >
              <Heart size={20} />
              {wishlist_items.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-raios-primary rounded-full text-[10px] font-mono flex items-center justify-center text-raios-text-high">
                  {wishlist_items.length > 9 ? '9+' : wishlist_items.length}
                </span>
              )}
            </motion.button>

            {/* Cart */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => set_is_cart_open(true)}
              className="p-2 text-raios-text-support hover:text-raios-text-high transition-colors relative"
              aria-label="Carrito"
            >
              <ShoppingCart size={20} />
              {cart_items.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-raios-primary rounded-full text-[10px] font-mono flex items-center justify-center text-raios-text-high">
                  {cart_items.length > 9 ? '9+' : cart_items.length}
                </span>
              )}
            </motion.button>

            {/* User Avatar / Menu - Desktop */}
            {is_authenticated && (
              <>
                <div className="w-px h-6 bg-raios-text-support/20 mx-2" />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => set_is_menu_open(true)}
                  className="relative"
                  aria-label="Menú usuario"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden ring-2 ring-raios-text-support/30 hover:ring-raios-primary transition-all">
                    {user?.profile_image_url ? (
                      <img src={get_avatar_url(user.profile_image_url, AVATAR_SIZES.nav)} alt={user.full_name || 'Usuario'} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-raios-primary/20 flex items-center justify-center">
                        <User size={16} className="text-raios-primary" />
                      </div>
                    )}
                  </div>
                </motion.button>
              </>
            )}

            {/* CTA Button */}
            {is_authenticated ? (
              <Button variant="primary" size="sm" on_click={() => navigate('/publicar')}>
                Publicar Obra
              </Button>
            ) : (
              <Button variant="primary" size="sm" on_click={() => navigate('/auth')}>
                Iniciar Sesión
              </Button>
            )}
          </div>

          {/* Mobile Actions (derecha) */}
          <div className="flex md:hidden items-center gap-1">
            {/* Search - Mobile */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => set_is_search_open(true)}
              className="p-2 text-raios-text-support"
              aria-label="Buscar"
            >
              <Search size={22} />
            </motion.button>

            {/* Cart - Mobile */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => set_is_cart_open(true)}
              className="p-2 text-raios-text-support relative"
              aria-label="Carrito"
            >
              <ShoppingCart size={22} />
              {cart_items.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-raios-primary rounded-full text-[10px] font-mono flex items-center justify-center text-raios-text-high">
                  {cart_items.length > 9 ? '9+' : cart_items.length}
                </span>
              )}
            </motion.button>
          </div>
        </nav>
      </Container>

      {/* SearchOverlay - Portal al body */}
      <SearchOverlay
        is_open={is_search_open}
        on_close={() => {
          set_is_search_open(false);
          set_search_results(null);
        }}
        on_search={handle_search}
        on_select_result={handle_select_result}
        results={search_results}
        is_loading={is_search_loading}
        recent_searches={recent_searches}
      />

      {/* MenuPanel Híbrido - Portal al body */}
      <MenuPanel
        is_open={is_menu_open}
        on_close={() => set_is_menu_open(false)}
        is_authenticated={is_authenticated}
        user={is_authenticated ? {
          name: user?.full_name || user?.name || 'Usuario',
          username: user?.email || 'usuario',
          avatar_url: user?.profile_image_url || null,
          is_verified: false,
          is_artist: user?.is_artist || false,
        } : null}
        stats={{
          obras: 0,
          ventas: '$0',
          likes: wishlist_items.length,
        }}
        profile_setup_complete={profile_setup_complete}
        on_navigate={(path) => navigate(path)}
        on_logout={handle_logout}
        on_login={handle_login}
      />

      {/* CartPanel - Portal al body */}
      <CartPanel
        is_open={is_cart_open}
        on_close={() => set_is_cart_open(false)}
        items={cart_items.map(item => ({
          id: item.artwork?.id || item.artwork_id || item.id,
          title: item.artwork?.title || item.title,
          artist_name: item.artwork?.artist?.full_name || item.artwork?.artist_name || item.artist_name,
          price: item.artwork?.price || item.price,
          quantity: item.quantity,
          image_url: item.artwork?.images?.[0] || item.artwork?.image_url || item.image_url || item.images?.[0],
        }))}
        on_update_quantity={handle_update_quantity}
        on_remove_item={handle_remove_item}
        on_checkout={handle_checkout}
        on_continue_shopping={handle_continue_shopping}
      />
    </header>
  );
};

export default Navbar;
